import React, { useEffect } from 'react';

import { useRentalManagerRentPaymentWebApiHandler_getListingsEligibleForPayments } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { useAuth } from '../AuthProvider';
import { Button, ButtonGroup, Heading, Icon, List, MediaObject, Page, Paragraph } from '@zillow/constellation-10';
import { IconCalendarClockFilled, IconDownloadFilled, IconHomeTypeFilled } from '@zillow/constellation-icons';

import GetStartedSVG from '../images/GetStarted';
import SelectProperty from '../components/Modals/SelectProperty';
import { client } from '../rentalManagerClient';
import {
  emitClickAddNewPropertyEvent,
  emitClickStartCollectingRentButtonEvent,
  emitEmptyStatePageViewEvent,
} from '../analytics/events';

const SellingPoints = [
  {
    icon: <Icon size="lg" render={<IconCalendarClockFilled />} />,
    heading: 'Send reminders',
    subheading: 'See an overdue payment? Use Zillow to automatically send a rent reminder.',
  },
  {
    icon: <Icon size="lg" render={<IconDownloadFilled />} />,
    heading: 'Export data',
    subheading: 'Swiftly download your payment details for your own records, or for sharing with an accountant.',
  },
  {
    icon: <Icon size="lg" render={<IconHomeTypeFilled />} />,
    heading: 'Get the full picture fast',
    subheading: 'Review rental income and manage your payment terms, all in one place.',
  },
];

const GetStartedPage = () => {
  const { data } = useRentalManagerRentPaymentWebApiHandler_getListingsEligibleForPayments({ httpClient: client });
  const { user } = useAuth();
  const [isSelectPropertyModalOpen, setIsSelectPropertyModalOpen] = React.useState(false);

  useEffect(() => {
    if (user) {
      emitEmptyStatePageViewEvent(user);
    }
  }, [user]);

  const handleAddPropertyClick = (e: React.MouseEvent<HTMLButtonElement>, fireClickstreamEvent = false) => {
    const pathToRoute = `/rental-manager/properties?redirectTo=payments`;
    if (fireClickstreamEvent && user) {
      emitClickAddNewPropertyEvent(user);
    }

    if (e.metaKey || e.ctrlKey) {
      window.open(pathToRoute, '_blank', 'noopener, noreferrer');
    } else {
      window.location.href = pathToRoute;
    }
  };

  return (
    <Page.Root data-testid="GetStartedPage" css={{ backgroundColor: 'bg.neutral' }}>
      <Page.Content css={{ paddingTop: 'loosest' }}>
        <GetStartedSVG />

        <Heading level={1} textStyle="heading-xs" css={{ marginTop: 'default', marginBottom: 'tightest' }}>
          Collect payments online for free
        </Heading>

        <Paragraph css={{ marginBottom: 'default' }}>
          Get rent, security deposits, and utility payments sent straight to your bank account.
        </Paragraph>

        <List.Root appearance="none" css={{ marginBottom: 'default' }}>
          {SellingPoints.map((point) => (
            <List.Item key={point.heading} css={{ marginBottom: 'loose' }}>
              <MediaObject media={point.icon}>
                <Heading level={2} textStyle="body-lg-bold" css={{ marginBottom: 'tighter' }}>
                  {point.heading}
                </Heading>
                <Paragraph>{point.subheading}</Paragraph>
              </MediaObject>
            </List.Item>
          ))}
        </List.Root>

        <ButtonGroup aria-label="Get started with payments">
          {data?.listings.length === 0 ? (
            <Button
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (user) {
                  emitClickStartCollectingRentButtonEvent(user);
                }
                handleAddPropertyClick(e);
              }}
              tone="brand"
              emphasis="filled"
            >
              Start collecting rent
            </Button>
          ) : (
            <>
              <Button
                tone="brand"
                emphasis="filled"
                onClick={() => {
                  if (user) {
                    emitClickStartCollectingRentButtonEvent(user);
                  }
                  setIsSelectPropertyModalOpen(true);
                }}
              >
                Start collecting rent
              </Button>
              {isSelectPropertyModalOpen && (
                <SelectProperty
                  onClose={() => setIsSelectPropertyModalOpen(false)}
                  onAddPropertyClick={(e: React.MouseEvent<HTMLButtonElement>) => handleAddPropertyClick(e, true)}
                />
              )}
            </>
          )}
          <Button asChild>
            <a target="_blank" rel="noreferrer" href="https://www.zillow.com/rental-manager/collect-rent-online/">
              Learn more
            </a>
          </Button>
        </ButtonGroup>
      </Page.Content>
    </Page.Root>
  );
};

export default GetStartedPage;
